import React, { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, KeyRound, Trash2, ShieldCheck, MailCheck } from "lucide-react";

/**
 * Root-admin-only Team panel. Shown on the Super Admin dashboard when
 * `/auth/me` returned `is_root: true`. Every other admin never sees it.
 */
export function TeamPanel() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/administrators");
      setAdmins(r.data.administrators);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load team");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e?.preventDefault();
    setInviteBusy(true);
    try {
      await adminApi.post("/admin/administrators", {
        email: inviteEmail.trim(),
        name: inviteName.trim() || null,
      });
      toast.success(`Invitation emailed to ${inviteEmail.trim()}`);
      setInviteEmail(""); setInviteName(""); setInviteOpen(false);
      load();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Invite failed");
    } finally { setInviteBusy(false); }
  };

  const resetPassword = async (a) => {
    if (!window.confirm(`Send a password-reset email to ${a.email}?`)) return;
    try {
      await adminApi.post(`/admin/administrators/${a.id}/reset-password`);
      toast.success(`Reset email sent to ${a.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email} from the administrator team? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/administrators/${a.id}`);
      toast.success(`${a.email} removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Card className="mb-8 border-stone-200/80" data-testid="team-panel">
      <CardContent className="p-5">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-serif text-xl flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-800" />
              Administrator team
            </h2>
            <p className="caption-label mt-0.5">Root only — invite, reset, or remove other administrators.</p>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-emerald-800 hover:bg-emerald-900 text-amber-50"
            data-testid="invite-admin-button"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Invite administrator
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="py-2.5 pr-3 caption-label">Email</th>
                <th className="py-2.5 pr-3 caption-label">Name</th>
                <th className="py-2.5 pr-3 caption-label">Role</th>
                <th className="py-2.5 pr-3 caption-label text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="team-table-body">
              {loading && (
                <tr><td colSpan={4} className="py-6 text-center text-stone-500 text-sm">Loading…</td></tr>
              )}
              {!loading && admins.map(a => (
                <tr key={a.id} className="border-b border-stone-100" data-testid={`admin-row-${a.email}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs">{a.email}</td>
                  <td className="py-2.5 pr-3">{a.name || "—"}</td>
                  <td className="py-2.5 pr-3">
                    {a.is_root ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                        <ShieldCheck className="w-3 h-3" /> Root
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">
                        Administrator
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {!a.is_root && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Send password-reset email"
                            onClick={() => resetPassword(a)}
                            data-testid={`admin-reset-${a.email}`}
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Remove administrator"
                            onClick={() => remove(a)}
                            data-testid={`admin-delete-${a.email}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </Button>
                        </>
                      )}
                      {a.is_root && (
                        <span className="caption-label text-stone-400">immutable</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && admins.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-stone-500 text-sm">No administrators.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="invite-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <MailCheck className="w-5 h-5 text-emerald-800" /> Invite administrator
            </DialogTitle>
            <DialogDescription>
              They will receive an email with a one-time link (valid 24 hours) to set their own password.
              No plaintext password is ever sent or stored.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-3">
            <div>
              <Label className="caption-label">Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@example.com"
                required
                data-testid="invite-admin-email"
              />
            </div>
            <div>
              <Label className="caption-label">Name (optional)</Label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Full name"
                data-testid="invite-admin-name"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={inviteBusy || !inviteEmail.trim()}
                className="bg-emerald-800 hover:bg-emerald-900"
                data-testid="invite-admin-submit"
              >
                <UserPlus className="w-4 h-4 mr-1" /> Send invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
